import { useRedirectToCheckout } from "@/features/Checkout/hooks/useRedirectToCheckout";
import { SubscriptionPanel } from "@/features/Subscriptions/components/SubscriptionPanel/SubscriptionPanel";
import { useProducts } from "@/features/Subscriptions/hooks/useProducts";
import { goto_checkout_event } from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import {
  Button,
  Flex,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Icon } from "@pufflig/prompt-studio-ui";
import { Suspense, useState } from "react";

interface UpsellModalProps {
  Image: React.ReactNode;
  title: string;
  descriptions: string[];
  features: string[];
  isOpen: boolean;
  onClose: () => void;
}

export const UpsellModal = (props: UpsellModalProps) => {
  const { title, descriptions = [], isOpen, Image, features = [], onClose } = props;
  const [loading, setIsLoading] = useState(false);
  const analytics = useAnalytics();

  const {
    onOpen: onOpenSubscriptionPanel,
    onClose: onCloseSubscriptionPanel,
    isOpen: isOpenSubscriptionPanel,
  } = useDisclosure();

  const redirectToCheckout = useRedirectToCheckout();
  const { data: products } = useProducts();
  const proSubscription = products?.find((product) => product.subscription_type === "pro");
  const proPriceMonthlyId = proSubscription?.prices?.find((price) => price.interval === "monthly")?.stripe_price_id;

  const goToCheckout = async () => {
    analytics.capture(goto_checkout_event, { from: "pricing_selector" });
    setIsLoading(true);
    if (proPriceMonthlyId === undefined) {
      throw new Error("Prices could not be loaded");
    }
    const lineItems = [
      {
        price: proPriceMonthlyId,
        quantity: 1,
      },
    ];
    await redirectToCheckout(lineItems, true);
    setIsLoading(false);
  };

  const openSubscriptionPanel = () => {
    onOpenSubscriptionPanel();
    onClose();
  };

  return (
    <>
      <Modal variant="large" size="5xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader padding="spacing-lg">
            <Text textStyle="heading/2">{title}</Text>
            <ModalCloseButton mt="4px" />
          </ModalHeader>
          <ModalBody>
            <Flex gap="2rem">
              <Flex>
                <Suspense>{Image}</Suspense>
              </Flex>
              <Grid templateColumns="repeat(4, 1fr)" width="100%">
                {descriptions.map((description, index) => {
                  return (
                    <GridItem key={index} rowSpan={1} colSpan={4}>
                      <Text textStyle="label/L/regular">{description}</Text>
                    </GridItem>
                  );
                })}
                <GridItem rowSpan={1} colSpan={4}>
                  <Grid templateColumns="repeat(2, 1fr)" gap="spacing-xs">
                    {features.map((item, index) => {
                      return (
                        <GridItem key={index} display="flex" textStyle="label/M/regular" gap="spacing-xs">
                          <Icon name="check" size="sm" />
                          {item}
                        </GridItem>
                      );
                    })}
                  </Grid>
                </GridItem>
              </Grid>
            </Flex>
          </ModalBody>
          <ModalFooter display="flex" gap="12px">
            <Button size="lg" onClick={openSubscriptionPanel}>
              Manage subscription
            </Button>
            <Button size="lg" isLoading={loading} onClick={goToCheckout} variant="primary">
              Upgrade
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <SubscriptionPanel onClose={onCloseSubscriptionPanel} isOpen={isOpenSubscriptionPanel} />
    </>
  );
};
